// Rollen-Modell 2026-10 (siehe api/src/Utils/Roles.php):
// bauleiter/back_office/finanzen/admin = intern (@gpsbau.de bzw.
// Admin-Domains), vertrieb = extern (eigene Domain, per-BV gescoped).
export type UserRole = 'bauleiter' | 'back_office' | 'finanzen' | 'admin' | 'vertrieb';

export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  is_admin: boolean;
  /** Feldname historisch — Semantik jetzt: Antrags-Freigabe (back_office/finanzen/admin). */
  is_approver: boolean;
  /** Büro-Rollen (globale Rechnungen + Dokumente-Schreibzugriff). */
  is_office: boolean;
  /** Zahlungs-Rechte: Freigabe/bezahlt/Lexware (finanzen/admin). */
  is_finanzen: boolean;
  is_bauleiter: boolean;
  /** Vertriebs-Konto (Massive Wohnbau, extern): per-BV gescoped. */
  is_vertrieb?: boolean;
  is_active?: boolean;
  exclude_from_sync?: boolean;
  microsoft_365_email?: string;
  microsoft_365_user_id?: string;
  // Effektive Modul-Zugriffe (vom Backend berechnet: global UND Rolle/Override)
  modules?: Record<string, boolean>;
  // Admin-Ansicht: rohe Overrides {"module_key": "on"|"off"}, fehlender Key = erben
  module_overrides?: Record<string, 'on' | 'off'> | null;
  // Verknüpfter Master-Kontakt (Bauleiter-Zuordnung), admin-gepflegt
  contact_id?: number | null;
  contact_name?: string | null;
}

export interface ModuleConfig {
  module_key: string;
  is_enabled: boolean;
  role_defaults: Record<string, boolean>;
  sort_order?: number;
}

/** Zeile der Mail-Ingest-Staging-Liste (admin/mail-ingest, Modul 'rechnungen'). */
export interface MailIngestStagingRow {
  id: number;
  original_name: string | null;
  sender_name: string | null;
  sender_email: string | null;
  subject: string | null;
  /** Berlin-Wanduhrzeit (vom Server aus der UTC-Achse konvertiert). */
  received_at: string;
  created_at: string;
  status: 'pending' | 'processing' | 'done' | 'no_invoice' | 'failed' | 'ignored';
  skip_reason: string | null;
  error_message: string | null;
  match_confidence: string | null;
  matched_bauvorhaben_id: number | null;
  matched_bauvorhaben_name?: string | null;
  invoice_id: number | null;
  invoice_number?: string | null;
  extraction_job_id: number | null;
  file_size: number | null;
}

export interface AuthResponse {
  user: User;
  tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
  };
}

export interface Address {
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

export interface Contact {
  id: number;
  global_uuid: string;
  
  // Name fields
  first_name?: string;
  last_name?: string;
  display_name?: string;
  title?: string;
  
  // Company
  company_name?: string;
  department?: string;
  job_title?: string;
  
  // Multiple emails
  email_address?: string;
  secondary_email?: string;
  tertiary_email?: string;
  
  // Multiple phones
  mobile_phone?: string;
  business_phone?: string;
  business_phone_2?: string;
  home_phone?: string;
  home_phone_2?: string;
  fax?: string;
  pager?: string;
  
  // Business address
  street?: string;
  city?: string;
  postal_code?: string;
  state?: string;
  country?: string;
  
  // Home address
  home_street?: string;
  home_city?: string;
  home_postal_code?: string;
  home_state?: string;
  home_country?: string;
  
  // Other address
  other_street?: string;
  other_city?: string;
  other_postal_code?: string;
  other_state?: string;
  other_country?: string;
  
  // Other fields
  business_homepage?: string;
  personal_notes?: string;
  birthday?: string;
  
  // Sync fields
  content_hash?: string;
  created_at: string;
  updated_at: string;

  // Sync origin (only set on rows that came in from a specific user's
  // Outlook via `SyncService` — used by the merge-into-existing flow
  // to know which mailbox to clean up).
  source_user_email?: string;
  ms_contact_id?: string;

  // Pending proposal
  pending_proposals_count?: number;
  pending_proposal_data?: Partial<Contact>;
}

export type ProposalStatus = 'pending' | 'approved' | 'rejected';
export type ProposalAction = 'create' | 'update' | 'delete';

export interface PotentialDuplicate {
  id: number;
  global_uuid: string;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  company_name?: string | null;
  email_address?: string | null;
  secondary_email?: string | null;
  tertiary_email?: string | null;
  mobile_phone?: string | null;
  business_phone?: string | null;
  business_phone_2?: string | null;
  home_phone?: string | null;
  home_phone_2?: string | null;
  content_hash?: string | null;
  match_score: number;
  match_reasons: string[];
}

export interface Proposal {
  id: number;
  proposed_by_user_id: number;
  proposed_by_name?: string;
  proposed_by_email?: string;
  action: ProposalAction;
  contact_id?: number;
  new_contact_data: Partial<Contact>;
  changes_summary?: string[];
  /** Master contacts that look like the same person as the proposal. */
  potential_duplicates?: PotentialDuplicate[];
  status: ProposalStatus;
  review_notes?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

export type DetectedChangeStatus = 'detected' | 'converted_to_proposal' | 'ignored' | 'approved_direct' | 'rejected';

export interface DetectedChange {
  id: number;
  detected_from_user_id: number;
  detected_from_name?: string;
  detected_from_email?: string;
  source_contact_id?: number;
  action: ProposalAction;
  captured_data: Partial<Contact>;
  ms_graph_contact_id?: string;
  status: DetectedChangeStatus;
  detected_at: string;
  processed_at?: string;
}

export interface PaginationInfo {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: PaginationInfo;
}

export interface SyncStatus {
  recent_operations: SyncLog[];
  user_states: UserSyncState[];
  pending_changes: {
    proposals: number;
    detected_changes: number;
  };
}

export interface SyncLog {
  id: number;
  operation_type: string;
  status: string;
  target_user_email?: string;
  details?: Record<string, unknown>;
  error_message?: string;
  started_at: string;
  completed_at?: string;
}

export interface UserSyncState {
  user_id: number;
  name: string;
  email: string;
  last_push_at?: string;
  last_pull_at?: string;
  is_syncing: boolean;
  last_error?: string;
  last_error_at?: string;
}

// Calendar/Event Types
export type EventType =
  | 'company_event'
  | 'holiday'
  | 'meeting'
  | 'birthday'
  | 'arzttermin'
  | 'urlaub'
  | 'other'
  | 'privat'
  | 'physio'
  | 'krank'
  | 'kinder_kindergarten_schule'
  | 'betriebsurlaub_subunternehmer'
  | 'sonderurlaub'
  | 'baustellentermin'
  | 'privat_kfz';

export interface CalendarEvent {
  id: number;
  global_uuid: string;
  title: string;
  description?: string;
  location?: string;
  event_type: EventType;
  start_date: string;
  end_date?: string;
  is_all_day: boolean;
  timezone: string;
  is_public: boolean;
  target_departments?: string[];
  content_hash?: string;
  ms_graph_event_id?: string;
  created_by_user_id: number;
  created_by_name?: string;
  approved_by_user_id?: number;
  approved_by_name?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  // Pending proposal fields (when shown in calendar before approval)
  proposal_id?: number;
  proposal_status?: string;
  proposal_action?: string;
  // Contact birthday overlay (not a real calendar event)
  contact_id?: number;
  contact_birthday?: string;
  birthday_age?: number;
}

export interface EventProposal {
  id: number;
  proposed_by_user_id: number;
  proposed_by_name?: string;
  proposed_by_email?: string;
  action: ProposalAction;
  event_id?: number;
  new_event_data: Partial<CalendarEvent>;
  existing_title?: string;
  existing_start_date?: string;
  status: ProposalStatus;
  review_notes?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ImportResult {
  imported: Array<{
    proposal_id: number;
    contact: string;
    action: string;
    reason: string;
    status: string;
  }>;
  errors: Array<{
    contact: string;
    error: string;
  }>;
  total: number;
  success_count: number;
  error_count: number;
}

// Bauvorhaben (Phase 1: Stammdaten)
export type BauvorhabenStatus = 'geplant' | 'aktiv' | 'abgeschlossen' | 'abgebrochen';
export type BauvorhabenContactRole = 'bauherr' | 'gewerk' | 'sonstiges';

export interface Bauvorhaben {
  id: number;
  name: string;
  project_number?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  status: BauvorhabenStatus;
  start_date?: string | null;
  end_date?: string | null;
  bauleiter_user_id?: number | null;
  bauleiter_name?: string | null;
  bauleiter_contact_id?: number | null;
  vertriebler_contact_id?: number | null;
  vertriebler_name?: string | null;
  bauherren_count?: number;
  gewerke_fertig_count?: number;
  gewerke_in_arbeit_count?: number;
  /** Nenner: aktive Katalog-Gewerke (global). */
  gewerke_total_count?: number;
  /** Gewichtet: fertig = 1, in_arbeit = 0,5 (0–100). */
  progress_percent?: number;
  notes?: string | null;
  created_by_user_id?: number;
  created_at: string;
  updated_at: string;
}

export interface BauvorhabenContact {
  link_id: number;
  contact_id: number;
  role: BauvorhabenContactRole;
  gewerk_label: string;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  email_address?: string | null;
  business_phone?: string | null;
}

export interface BauvorhabenDetail {
  bauvorhaben: Bauvorhaben;
  contacts: BauvorhabenContact[];
}

// PUT-Antwort: Bauvorhaben + Anzahl automatisch erzeugter Adress-Anträge
export interface BauvorhabenUpdateResult extends Bauvorhaben {
  address_proposals_created?: number;
}

// Bauvorhaben-Verknüpfung eines Kontakts (Kontakt-Detailseite, lesend)
export type ContactBauvorhabenRole = BauvorhabenContactRole | 'vertriebler';

export interface ContactBauvorhabenLink {
  id: number;
  name: string;
  status: BauvorhabenStatus;
  role: ContactBauvorhabenRole;
  /** '' bei bauherr/sonstiges/vertriebler */
  gewerk_label: string;
}

// Gewerk-Katalog (Bauvorhaben Phase 3)
export type GewerkStatus = 'zu_erledigen' | 'in_arbeit' | 'fertig';

export interface Gewerk {
  id: number;
  name: string;
  sort_order: number;
  is_active: boolean;
}

export interface GewerkPhase {
  id: number;
  name: string;
  sort_order: number;
  gewerke: Gewerk[];
}

export interface GewerkContactLink {
  link_id: number;
  contact_id: number;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  email_address?: string | null;
  business_phone?: string | null;
}

// Akkordeon-Payload je Bauvorhaben: aktive Gewerke mit Status, Prozess-
// Daten (Bautenstandsliste: Datum statt „erledigt") + Kontakten
export interface AccordionGewerk extends Gewerk {
  status: GewerkStatus;
  angefragt_am: string | null;      // DATE (YYYY-MM-DD)
  beauftragt_am: string | null;    // DATE (YYYY-MM-DD)
  fertiggestellt_am: string | null; // DATE (YYYY-MM-DD)
  note: string | null;
  dauer_tage?: number | null;      // abgeleitet: fertiggestellt − beauftragt
  tage_seit_beauftragt?: number | null; // abgeleitet (nur solange nicht fertig)
  contacts: GewerkContactLink[];
}

export interface AccordionPhase extends GewerkPhase {
  gewerke: AccordionGewerk[];
}

/** Teil-Update eines Gewerks im Bauvorhaben (Bautenstandsliste).
 *  Status + Fertigstellungsdatum immer zusammen senden (API-Invariante). */
export interface GewerkUpdatePatch {
  status?: GewerkStatus;
  angefragt_am?: string | null;
  beauftragt_am?: string | null;
  fertiggestellt_am?: string | null;
  note?: string | null;
}

export interface AccordionPayload {
  phases: AccordionPhase[];
}

// ============================================================
// Dokumente (Modul 'dokumente'): pro Bauvorhaben strukturierte
// Ablage mit festen Kategorien — Ersatz für die Netzlaufwerk-
// Ordner. Bewusst OHNE Rechnungen (die bleiben im Rechnungen-
// Modul).
// ============================================================

export type DocumentCategory =
  | 'schriftwechsel'
  | 'bauamt'
  | 'architektur'
  | 'bauherr'
  | 'gewerke'
  | 'bilder'
  | 'abnahme'
  | 'abrechnung'
  | 'baudurchsprache'
  | 'medien'
  | 'sonstiges'
  | 'vertrag'
  // Klassifizierte Kategorie — nur Finanzen/Admin (Backend-Gate);
  // Selektoren rendern die Option nur für diese Rollen.
  | 'geschaeftsintern';

/** Eine Row je Datei; Multi-Upload = N Rows mit gemeinsamem Titel. */
export interface BauvorhabenDocument {
  id: number;
  bauvorhaben_id: number;
  bauvorhaben_name?: string | null;
  category: DocumentCategory;
  title: string | null;
  file_path: string | null;
  original_name: string | null;
  file_size: number | null;
  file_mime: string | null;
  gewerk_id: number | null;
  gewerk_name?: string | null;
  taken_at: string | null; // DATE (YYYY-MM-DD)
  uploaded_by_user_id: number | null;
  uploaded_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

/** Summary der Per-BV-Variante (GET dokumente?bauvorhaben_id=). */
export interface DokumenteSummary {
  total_count: number;
  image_count: number;
  counts_by_category: Record<DocumentCategory, number>;
}

/** Payload der Per-BV-Variante (konsumiert von der BV-Karte). */
export interface BauvorhabenDokumente {
  documents: BauvorhabenDocument[];
  summary: DokumenteSummary;
}

/** Upload-Response: partial success wie Rechnungen-Anlagen. */
export interface DokumenteUploadResult {
  documents: BauvorhabenDocument[];
  errors: { name: string; error: string }[];
}

// ============================================================
// Vertragsunterlagen-Checkliste (A-Checkliste-MWb-Ersatz): der
// Vertrieb pflegt Status + Eingangsdatum je Position selbst —
// 'vollständig' ⇔ received_date (gleiche Datum-Philosophie wie
// fertig⇔Datum bei den Gewerken).
// ============================================================

export type VertragsunterlagenStatus = 'offen' | 'eingereicht' | 'vollständig';

export interface Vertragsunterlage {
  item_key: string;
  label: string;
  status: VertragsunterlagenStatus;
  received_date: string | null; // DATE (YYYY-MM-DD)
  note: string | null;
  updated_by_user_id: number | null;
}

export interface BauvorhabenVertragsunterlagen {
  items: Vertragsunterlage[];
  total_count: number;
  complete_count: number;
}

export interface VertragsunterlagePatch {
  status?: VertragsunterlagenStatus;
  received_date?: string | null;
  note?: string | null;
}
export interface DokumentePagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/** de-DE-Labels der festen Kategorien (Reihenfolge = Anzeige-Reihenfolge). */
export const documentCategoryLabels: Record<DocumentCategory, string> = {
  schriftwechsel: 'Schriftwechsel',
  bauamt: 'Bauamt',
  architektur: 'Architektur',
  bauherr: 'Bauherr',
  gewerke: 'Gewerke',
  bilder: 'Bilder',
  abnahme: 'Abnahme',
  abrechnung: 'Abrechnung',
  baudurchsprache: 'Baudurchsprache',
  medien: 'Medien',
  sonstiges: 'Sonstiges',
  vertrag: 'Vertrag',
  geschaeftsintern: 'Geschäftsintern',
};

// ============================================================
// Rechnungen (Modul 'rechnungen', Phase 1)
// Eingangs-/Ausgangsrechnungen teilen sich ein Entität (direction).
// Status-Workflow siehe InvoiceController::TRANSITIONS.
// ============================================================

export type InvoiceDirection = 'eingang' | 'ausgang';

export type InvoiceStatus =
  | 'neu_eingegangen'
  | 'erfasst'
  | 'formal_geprueft'
  | 'freigegeben'
  | 'korrektur_erforderlich'
  | 'abgelehnt'
  | 'an_lexware_uebergeben';

export type PaymentStatus = 'offen' | 'bezahlt';

export type InvoiceTransitionAction =
  | 'email_received'
  | 'formal_check'
  | 'approve'
  | 'request_correction'
  | 'reject'
  | 'reopen_review'
  | 'mark_exported'
  | 'reopen_lexware'
  | 'mark_paid'
  | 'mark_unpaid';

/** Beträge kommen als String aus PDO (DECIMAL) — Frontend formatiert. */
export interface Invoice {
  id: number;
  direction: InvoiceDirection;
  invoice_number: string;
  status: InvoiceStatus;
  payment_status: PaymentStatus;
  /** DECIMAL → string (de-DE-Formatierung im Frontend) */
  amount_gross: string;
  /** Grid-Spalten (seit Bulk-Endpoint auch in der Liste) — Liste = String
   *  (DECIMAL), Detail (show()) liefert Zahlen; NULL wenn nicht gesetzt. */
  amount_net?: string | number | null;
  amount_tax?: string | number | null;
  tax_rate_percent?: string | number | null;
  invoice_date: string;
  due_date?: string | null;
  description?: string | null;
  /** Dokumentation/Bericht (umsgenutztes Notizen-Feld, Phase 3). */
  notes?: string | null;
  currency: string;
  file_path?: string | null;
  file_original_name?: string | null;
  file_size?: number | null;
  file_mime?: string | null;
  bauvorhaben_id: number | null;
  bauvorhaben_name?: string | null;
  project_number?: string | null;
  /** Für can_review — wird serverseitig berechnet, das UI dupliziert die Rollenlogik nicht. */
  bauleiter_user_id: number | null;
  gewerk_id: number | null;
  gewerk_name?: string | null;
  contact_id: number | null;
  contact_name?: string | null;
  /** null = maschinell aus dem E-Mail-Eingang (source 'email'). */
  created_by_user_id: number | null;
  created_by_name?: string | null;
  /** Phase 2.5: Quellerfassung — 'email' = E-Mail-Rechnungseingang
   *  (external_ref = Graph-Message-ID); 'manuell' = Backend-Default. */
  source?: 'manuell' | 'email';
  external_ref?: string | null;
  lexware_exported_at?: string | null;
  created_at: string;
  updated_at: string;
  /** Phase 3: Anzahl Anlagen (invoice_files); has_file = attachment_count > 0. */
  attachment_count?: number;
  has_file: boolean;
  can_review: boolean;
}

/** Payload für create/update — Beträge als Zahlen (NULL = nicht gesetzt). */
export interface InvoiceWritePayload {
  direction?: InvoiceDirection;
  invoice_number?: string;
  contact_id?: number | null;
  bauvorhaben_id?: number | null;
  gewerk_id?: number | null;
  amount_net?: number | null;
  amount_tax?: number | null;
  amount_gross?: number;
  tax_rate_percent?: number | null;
  invoice_date?: string;
  due_date?: string | null;
  description?: string | null;
  notes?: string | null;
}

export interface InvoiceStatusLogEntry {
  id: number;
  from_status: string | null;
  to_status: string;
  action: string;
  comment?: string | null;
  created_at: string;
  user_name?: string | null;
}

export interface InvoiceComment {
  id: number;
  comment: string;
  is_internal: boolean;
  created_at: string;
  user_id: number;
  user_name?: string | null;
  user_role?: string | null;
}

export interface InvoiceDetail extends Invoice {
  notes?: string | null;
  uploaded_by_user_id: number | null;
  uploaded_by_name?: string | null;
  source: 'manuell' | 'email';
  paid_at?: string | null;
  lexware_exported_by_name?: string | null;
  status_log: InvoiceStatusLogEntry[];
  comments: InvoiceComment[];
}

/** Anlage (invoice_files, Phase 3) — mehrere Dateien je Rechnung. */
export interface InvoiceFile {
  id: number;
  invoice_id: number;
  file_path: string | null;
  original_name: string | null;
  file_size: number | null;
  file_mime: string | null;
  is_primary: boolean;
  uploaded_by_user_id: number | null;
  uploaded_by_name?: string | null;
  created_at: string;
}

export interface InvoiceDetailResponse {
  invoice: InvoiceDetail;
  status_log: InvoiceStatusLogEntry[];
  comments: InvoiceComment[];
  attachments?: InvoiceFile[];
}

/** Ergebnis des Anlagen-Uploads (POST rechnungen/{id}/files) — partial success. */
export interface InvoiceFilesResult {
  files: InvoiceFile[];
  errors: Array<{ name: string; error: string }>;
}

/** Finanz-Summen je Bauvorhaben (BV-Detailkarte); Werte als Strings (DECIMAL). */
export interface BauvorhabenInvoiceSummary {
  sum_offen: string;
  sum_freigegeben: string;
  sum_gesamt: string;
  count_offen: number;
  count_offen_overdue: number;
  total_count: number;
}

export interface BauvorhabenRechnungen {
  invoices: Invoice[];
  summary: BauvorhabenInvoiceSummary;
}

/** Ergebnis des Bulk-Imports (POST rechnungen/bulk) — partial success. */
export interface InvoiceBulkResult {
  created: number;
  skipped: number;
  errors: Array<{ row: number; invoice_number: string; error: string }>;
}

// ============================================================
// Dashboard (GET dashboard — Aggregat „Offene Aufgaben" je Rolle)
// ============================================================

/** Admin-Sicht (Buchhaltung): Zähler je Status mit Handlungsbedarf. */
export interface DashboardAdminCounts {
  neu_eingegangen: number;
  erfasst: number;
  korrektur_erforderlich: number;
  freigegeben: number;
  ueberfaellig: number;
  /** Mail-Eingang ohne BV-Zuordnung (source='email'), Handlungsbedarf. */
  ohne_bauvorhaben: number;
}

/** Bauleiter-Sicht: Rechnungen zur Freigabe auf eigenen Bauvorhaben. */
export interface DashboardFreigabeItem {
  id: number;
  invoice_number: string;
  direction: InvoiceDirection;
  amount_gross: string;
  invoice_date: string;
  bauvorhaben_name: string;
}

export interface DashboardInvoicesOffice {
  scope: 'office';
  counts: DashboardAdminCounts;
}

export interface DashboardInvoicesBauleiter {
  scope: 'bauleiter';
  zur_freigabe_count: number;
  zur_freigabe: DashboardFreigabeItem[];
}

export type DashboardInvoices = DashboardInvoicesOffice | DashboardInvoicesBauleiter;

/** Pendende Anträge (approver+). */
export interface DashboardAntraege {
  pending_contacts: number;
  pending_events: number;
}

export interface DashboardEvent {
  id: number;
  title: string;
  event_type: EventType;
  start_date: string;
  end_date?: string | null;
  is_all_day: boolean;
}

/** Mail-Eingang: Staging-Eintrag im Dashboard (Karte „E-Mail-Eingang"). */
export interface DashboardMailIngestItem {
  id: number;
  original_name: string | null;
  status: MailIngestStagingRow['status'];
  skip_reason: string | null;
  error_message: string | null;
  match_confidence: string | null;
  matched_bauvorhaben_id: number | null;
  matched_bauvorhaben_name?: string | null;
  invoice_id: number | null;
  sender_email: string | null;
  subject: string | null;
  /** Berlin-Wanduhrzeit (vom Server aus der UTC-Achse konvertiert). */
  received_at_berlin: string | null;
}

export interface DashboardMailIngest {
  ohne_rechnung: number;
  letzte_abholung: string | null;
  letzter_poll: string | null;
  letzter_fehler: string | null;
  mailbox: string | null;
  items: DashboardMailIngestItem[];
}

/** KI-Extraktion (approver+): Datenblatt-Reviews + fehlgeschlagene Jobs. */
export interface DashboardKiFehlerItem {
  id: number;
  bauvorhaben_id: number | null;
  bauvorhaben_name: string | null;
  error_message: string | null;
  updated_at: string;
}

export interface DashboardKiExtraktion {
  scope: 'review';
  zur_pruefung: number;
  fehlgeschlagen: number;
  fehler_items: DashboardKiFehlerItem[];
}

/** Mein Bauvorhaben (Bauleiter/Vertrieb): Fortschritt + offene Positionen. */
export interface DashboardBauvorhabenItem {
  id: number;
  name: string;
  project_number: string | null;
  status: string;
  progress_percent: number;
  gewerke_offen: number;
  vertragsunterlagen_offen: number;
}

export interface DashboardBauvorhaben {
  scope: 'bauleiter' | 'vertrieb';
  items: DashboardBauvorhabenItem[];
}

export interface DashboardData {
  invoices?: DashboardInvoices;
  antraege?: DashboardAntraege;
  my_pending_proposals?: number;
  events?: DashboardEvent[];
  mail_ingest?: DashboardMailIngest;
  ki_extraktion?: DashboardKiExtraktion;
  bauvorhaben?: DashboardBauvorhaben;
}

// ============================================================
// Datenblatt + KI-Extraktion (Phase 2)
// Das 4-seitige „_Datenblatt_“-Formular wird app-nativ gepflegt;
// ein hochgeladenes Datenblatt-PDF kann per KI (Mac-mini-Worker +
// lokales Ollama-Modell) extrahiert und nach menschlicher Prüfung
// übernommen werden. Bauherr-Kontaktdaten laufen IMMER über den
// Proposal-Workflow — die Extraktion erzeugt nur Anträge.
// Feldkatalog-Spiegel von ExtractionController::DATENBLATT_FIELDS.
// ============================================================

/** Tri-State der Unterlagen-Positionen (Datenblatt-Kästchen). */
export type DatenblattUnterlagenStatus = 'offen' | 'liegt_vor' | 'beauftragt' | 'wird_beauftragt';

export type Effizienzstandard =
  | 'geg'
  | 'effizienzstandard_a_plus'
  | 'kfw40_nicht_foerderfaehig'
  | 'kfw40_lca'
  | 'kfw40_qng'
  | 'eh55';

/** Flacher Datenblatt-Feldkatalog (Keys = DB-Spalten). Zahlen kommen
 *  als String aus PDO (DECIMAL), Datumsfelder als YYYY-MM-DD. */
export interface DatenblattFelder {
  gemarkung_flurstueck: string | null;
  erstkontakt_am: string | null;
  vertragssumme_brutto: string | null;
  haustyp: string | null;
  vertrag_abgeschlossen_am: string | null;
  wohnflaeche_m2: string | null;
  gesamtflaeche_m2: string | null;
  effizienzstandard: Effizienzstandard | null;
  bodengutachten: DatenblattUnterlagenStatus;
  bodengutachten_von: string | null;
  hoehennivellement: DatenblattUnterlagenStatus;
  hoehennivellement_von: string | null;
  bauantrag_extern: boolean;
  bauantrag_extern_von: string | null;
  baugenehmigung_vorhanden: boolean;
  kfw_baubegleitung: 'offen' | 'ean50' | 'andere';
  beauftragung_durch_bauherr: boolean;
  auftragsformular_unterzeichnet: boolean;
  zusatzkostenblatt: boolean;
  schallschutz: boolean;
  angebot_garage: boolean;
  angebot_garage_von: string | null;
  sonstige_angebote: string | null;
  fremdmakler: boolean;
  gebaut_von: 'gps' | 'kabel' | null;
  infoblaetter: string[];
  interessenten_quellen: string[];
  interessenten_hinweis: string | null;
  anmerkungen: string | null;
}

/** GET/PUT bauvorhaben/{id}/datenblatt → data. */
export interface Datenblatt extends DatenblattFelder {
  planer_contact_id: number | null;
  kalkulator_contact_id: number | null;
  planer_name: string | null;
  kalkulator_name: string | null;
  /** Existiert die lazy Zeile schon (false = reine Defaults)? */
  exists: boolean;
  updated_at: string | null;
}

/** Formular-Vokabulare (vom Backend eingebettet, damit Labels und
 *  Server-Validierung aus einer Quelle kommen). */
export interface DatenblattOptions {
  infoblaetter: Record<string, string>;
  interessenten_quellen: Record<string, string>;
  effizienzstandards: Effizienzstandard[];
}

export interface DatenblattResponse {
  datenblatt: Datenblatt;
  options: DatenblattOptions;
}

/** PUT-Payload: nur gesendete Felder werden übernommen (Partial). */
export type DatenblattPatch = Partial<DatenblattFelder & {
  planer_contact_id: number | null;
  kalkulator_contact_id: number | null;
}>;

// ---- KI-Extraktion -------------------------------------------------------

export type ExtractionJobStatus = 'pending' | 'processing' | 'done' | 'failed';

/** Bauherr-/Bauadresse-Gruppen des Extraktionsergebnisses (laufen über
 *  den Proposal-Workflow, nicht in die datenblatt-Tabelle). */
export interface ExtractionBauherr {
  name: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  festnetz: string | null;
  mobil_1: string | null;
  mobil_2: string | null;
  email_1: string | null;
  email_2: string | null;
}

export interface ExtractionBauadresse {
  strasse: string | null;
  plz: string | null;
  ort: string | null;
}

/** result_json des Workers / der Review-UI (vor dem Apply editierbar). */
export interface ExtractionResult extends DatenblattFelder {
  bauherr_1?: ExtractionBauherr | null;
  bauherr_2?: ExtractionBauherr | null;
  bauadresse?: ExtractionBauadresse | null;
}

export interface ExtractionJob {
  id: number;
  job_type: string;
  document_id: number;
  status: ExtractionJobStatus;
  error_message: string | null;
  claimed_at: string | null;
  finished_at: string | null;
  applied_at: string | null;
  created_at: string;
  original_name: string | null;
  document_title: string | null;
  /** Nur bei GET …/{jobId} (Review-UI). Für 'ek_positionen' liefert die API
   *  dieselbe Eigenschaft in einer anderen Form — per Cast in der
   *  Einkauf-Extraktion-Karte (EkPositionsResult). */
  result?: ExtractionResult | null;
}

/** Wie soll ein extrahierter Bauherr-Block übernommen werden? */
export interface ExtractionBauherrAssignment {
  mode: 'link' | 'create' | 'skip';
  contact_id?: number;
}

export interface ExtractionApplyPayload {
  /** Das (im Review ggf. korrigierte) Ergebnis. */
  result: ExtractionResult;
  assignments?: Partial<Record<'bauherr_1' | 'bauherr_2', ExtractionBauherrAssignment>>;
}

export interface ExtractionApplyResult {
  datenblatt_fields_written: number;
  bauadresse_applied: boolean;
  address_proposals_created: number;
  contact_proposal_ids: number[];
  skipped: string[];
}

// ============================================================
// Einkauf (Phase 3): Netto-EK-PDF → BV-Gewerke — Budget/EK/Skonto
// (Direct write, kein Proposal — nur Finanzen/Admin schreiben)
// ============================================================

export type EinkaufSource =
  | 'manuell'
  | 'ki_netto_ek_pdf'          // budget_source: aus der Summenübersicht des Netto-EK-PDF
  | 'eingangsrechnungen';     // ek_source: aus den erfassten Eingangsrechnungen

export interface EinkaufRowPatch {
  id: number;
  budget_netto?: number | string | null;
  ek_netto?: number | string | null;
  ek_source?: 'manuell' | 'eingangsrechnungen';
  skonto_percent?: number | string | null;
  eigenleistung_netto?: number | string | null;
  ek_note?: string | null;
}

/** Lieferant-Kontakt an einer EK-Zeile (reuse bauvorhaben_gewerk_contacts). */
export interface EinkaufLieferant {
  link_id: number;
  contact_id: number;
  display_name: string | null;
  company_name: string | null;
  email_address: string | null;
  business_phone: string | null;
  /** Σ Eingangsrechnungen dieses Kontakts an dieser Zeile. */
  rechnungen_summe_netto: number;
}

/** Die „Schlussrechnung"-Spalte des Excel: neueste Eingangsrechnung je Zeile. */
export interface EinkaufLetzteRechnung {
  id: number;
  invoice_number: string | null;
  invoice_date: string | null;
  amount_gross: string | null;
  contact_name: string | null;
  payment_status: string;
}

/** Eine Zeile der EK-Ebene (Katalog-Gewerk ODER Freitext). */
export interface EinkaufRow {
  id: number;
  gewerk_id: number | null;
  gewerk_name: string | null;
  is_free_text: boolean;
  phase_name: string | null;
  /** Sektionsnummer aus dem PDF ('01', '05', '50') — Anzeige + Sortierung. */
  quell_nummer: string | null;
  status: GewerkStatus;
  budget_netto: string | null;
  budget_source: EinkaufSource;
  ek_netto: string | null;
  ek_source: 'manuell' | 'eingangsrechnungen';
  skonto_percent: string | null;
  skonto_betrag: string | null;
  eigenleistung_netto: string | null;
  ek_note: string | null;
  /** budget − ek − eigenleistung (computed, nie gespeichert). */
  gewinn_verlust: string | null;
  eingangsrechnungen_summe_netto: number;
  letzte_rechnung: EinkaufLetzteRechnung | null;
  lieferanten: EinkaufLieferant[];
}

export interface EinkaufSummary {
  summe_budget: number;
  summe_ek: number;
  summe_eingangsrechnungen: number;
  summe_skonto: number;
  summe_gewinn_verlust: number;
  /** Σ ek_netto (dort inkl. Eigenleistung) — Basis der Abrechnung (Phase D). */
  herstellsumme: number;
  summe_status_fertig: number;
}

export interface EinkaufResponse {
  rows: EinkaufRow[];
  summary: EinkaufSummary;
  options: { default_skonto_percent: number };
  can_write: boolean;
}

// --- EK-Positionen-Extraktion (job_type 'ek_positionen') ---

/** Eine Zeile aus der „Summenübersicht Gewerke"-Seite des Netto-EK-PDF. */
export interface EkPositionsZeile {
  nummer: string;
  name: string;
  lst_netto: number;
  material_netto: number;
  gesamt_netto: number;
}

/** result_json des Workers für 'ek_positionen'. */
export interface EkPositionsResult {
  gewerke: EkPositionsZeile[];
  gesamtsumme_netto: number;
  source_meta?: { parser?: string; seiten?: number };
}

/** Wie soll die Zeile übernommen werden? Fehlende Einträge = Auto-Match. */
export interface EkLineMapping {
  mode: 'match' | 'freetext' | 'skip';
  /** mode 'match': Katalog-Gewerk-ID (zwingend). */
  gewerk_id?: number;
}

export interface EkApplyResult {
  applied: number;
  skipped: string[];
}

/** Job-Typ der Extraktions-Pipeline. */
export type ExtractionJobType = 'datenblatt' | 'rechnung' | 'ek_positionen';

/** Apply-Payload für 'ek_positionen' (Mapping je Zeilen-Index). */
export interface EkApplyPayload {
  result?: EkPositionsResult;
  mapping?: Record<string, EkLineMapping>;
}

// ============================================================
// Abrechnung (Phase 3): Excel-Abrechnungsprozess — Kopfzeile,
// Nachträge/Gutschriften, BNK, Zahlungsplan. Abgeleitete Werte
// (Formelkette) kommen im `summary` je GET berechnet vom API.
// ============================================================

export interface AbrechnungSettings {
  verkaufssumme_netto: number | null;
  verkaufssumme_brutto: number | null;
  fehlpositionen_vertrieb: number | null;
  vorplanungszuschlag: number | null;
  provision_gesamt: number | null;
  agk_percent: number;
  mwb_anteil_percent: number;
  bemerkung: string | null;
  updated_by_user_id: number | null;
  updated_by_user_name: string | null;
  updated_at: string | null;
}

export interface AbrechnungSettingsPatch {
  verkaufssumme_netto?: number | string | null;
  verkaufssumme_brutto?: number | string | null;
  fehlpositionen_vertrieb?: number | string | null;
  vorplanungszuschlag?: number | string | null;
  provision_gesamt?: number | string | null;
  agk_percent?: number | string;
  mwb_anteil_percent?: number | string;
  bemerkung?: string | null;
}

export interface NachtragRow {
  id: number;
  typ: 'nachtrag' | 'gutschrift';
  titel: string;
  nummer: string | null;
  datum: string | null;
  betrag_netto: number;
  betrag_brutto: number | null;
  bemerkung: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface BnkPosition {
  id: number;
  position: string | null;
  bezeichnung: string;
  menge: number | null;
  einheit: string | null;
  vk_einzelpreis_netto: number | null;
  vk_gesamt_netto: number | null;
  ek_einzelpreis_netto: number | null;
  ek_gesamt_netto: number | null;
  bemerkung: string | null;
}

export interface ZahlungsplanRow {
  id: number;
  art: 'ausgangsrechnung' | 'vertriebsprovision';
  sort_order: number;
  bezeichnung: string;
  baustatus: string | null;
  prozent: number | null;
  rechnungsnummer: string | null;
  rechnung_datum: string | null;
  betrag_netto: number | null;
  betrag_brutto: number | null;
  faellig_am: string | null;
  ist_bezahlt: boolean;
  bezahlt_am: string | null;
  zahlbetrag: number | null;
  invoice_id: number | null;
  invoice_number_linked: string | null;
  created_at: string;
  updated_at: string;
}

/** Berechnete Formelkette (nie gespeichert, je GET frisch). */
export interface AbrechnungSummary {
  herstellsumme: number;
  bnk_summe_ek: number;
  bnk_summe_vk: number;
  nachtraege_summe: number;
  gutschriften_summe: number;
  herstellkosten: number;
  agk_percent: number;
  agk_betrag: number;
  mwb_anteil_percent: number;
  agk_mwb_betrag: number;
  agk_gps_betrag: number;
  rohertrag: number | null;
  vertrieb_ergebnis_basis: number | null;
  provision_gesamt: number | null;
  provision_geplant: number;
  verkaufssumme_netto: number | null;
  verkaufssumme_brutto: number | null;
  gewinn_verlust: number | null;
}

export interface AbrechnungResponse {
  settings: AbrechnungSettings;
  nachtraege: NachtragRow[];
  bnk: BnkPosition[];
  zahlungsplan: ZahlungsplanRow[];
  summary: AbrechnungSummary;
  can_write: boolean | null;
}

/** POST/PUT-Payloads (PUT: alles optional). */
export interface NachtragPayload {
  typ?: 'nachtrag' | 'gutschrift';
  titel?: string;
  nummer?: string | null;
  datum?: string | null;
  betrag_netto?: number | string | null;
  betrag_brutto?: number | string | null;
  bemerkung?: string | null;
}

export interface BnkPayload {
  bezeichnung: string;
  position?: string | null;
  menge?: number | string | null;
  einheit?: string | null;
  vk_einzelpreis_netto?: number | string | null;
  vk_gesamt_netto?: number | string | null;
  ek_einzelpreis_netto?: number | string | null;
  ek_gesamt_netto?: number | string | null;
  bemerkung?: string | null;
}

export interface ZahlungsplanPayload {
  art?: 'ausgangsrechnung' | 'vertriebsprovision';
  bezeichnung: string;
  sort_order?: number;
  baustatus?: string | null;
  prozent?: number | string | null;
  rechnungsnummer?: string | null;
  rechnung_datum?: string | null;
  betrag_netto?: number | string | null;
  betrag_brutto?: number | string | null;
  faellig_am?: string | null;
  ist_bezahlt?: boolean;
  bezahlt_am?: string | null;
  zahlbetrag?: number | string | null;
  invoice_id?: number | null;
}
